'use client';

import { useEffect, useRef, useState } from 'react';

interface UseRemainingHeightProps {
  bottomPadding?: number;
}

/**
 * Hook to calculate the remaining height of an element relative to the viewport.
 * @example
 * const { ref, height } = useRemainingHeight({ bottomPadding: 16 });
 * @param options - The options for the hook.
 * @returns The remaining height of the element.
 */
const useRemainingHeight = (options?: UseRemainingHeightProps) => {
  const { bottomPadding = 0 } = options || {};
  const ref = useRef<HTMLElement>(null);
  const [height, setHeight] = useState<number | undefined>();

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const updateHeight = () => {
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const topOffset = rect.top;

      setHeight(Math.min(viewportHeight - topOffset - bottomPadding, viewportHeight));
    };

    // Initial calculation
    updateHeight();

    // Update on scroll and resize
    window.addEventListener('scroll', updateHeight);
    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('scroll', updateHeight);
      window.removeEventListener('resize', updateHeight);
    };
  }, [bottomPadding]);

  return { ref, height };
};

export default useRemainingHeight;
